import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

export const useRefreshControlStyles = makeStyles({
  wrapper: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
    gap: designTokens.spacing[1],
    flexShrink: "0",
    minWidth: "max-content",
  },

  timestampGroup: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    justifyContent: "start",
    width: "100%",
  },

  sectionLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "start",
    width: "130px",
    gap: designTokens.spacing[1],
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.bold,
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    color: designTokens.neutral[500],
    whiteSpace: "nowrap" as const,
  },

  timestampValueRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
  },

  timestampValue: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[700],
    whiteSpace: "nowrap" as const,
  },

  infoIconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0",
    background: "none",
    ...shorthands.border("none"),
    cursor: "pointer",
    color: designTokens.neutral[400],
    borderRadius: designTokens.borderRadius.sm,
    transition: `color ${designTokens.transition.fast}`,
    ":hover": {
      color: designTokens.neutral[600],
    },
    ":focus-visible": {
      outline: `2px solid ${designTokens.accent[500]}`,
      outlineOffset: "2px",
    },
  },

  errorText: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.semantic.error,
    maxWidth: "200px",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

export const useHistoryStyles = makeStyles({
  historyButton: {
    color: designTokens.neutral[500],
    minWidth: "unset",
    minHeight: "unset",
    width: "20px",
    height: "20px",
    padding: "0",
    ":hover": {
      color: designTokens.neutral[700],
    },
  },

  popoverSurface: {
    display: "flex",
    flexDirection: "column",
    padding: designTokens.spacing[4],
    minWidth: "560px",
    maxWidth: "680px",
    maxHeight: "360px",
    overflow: "hidden",
  },

  popoverHeader: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[3],
    flexShrink: 0,
    paddingBottom: designTokens.spacing[4],
    marginBottom: designTokens.spacing[3],
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
  },

  popoverBody: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
    scrollbarGutter: "stable",
  },

  popoverHeaderIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "32px",
    height: "32px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: designTokens.accent[50],
    color: designTokens.accent[500],
  },

  popoverHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flexGrow: 1,
    minWidth: 0,
  },

  popoverCloseButton: {
    flexShrink: 0,
    color: designTokens.neutral[500],
    ":hover": {
      color: designTokens.neutral[700],
    },
  },

  popoverTitle: {
    fontSize: designTokens.fontSize.base,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    lineHeight: "1.2",
  },

  popoverSubtitle: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    lineHeight: "1.2",
  },

  loadingRow: {
    display: "flex",
    justifyContent: "center",
    padding: designTokens.spacing[4],
  },

  emptyText: {
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[500],
  },

  // Header table is non-scrolling, body table scrolls beneath it. Both use
  // fixed layout with same column widths so they align. Header reserves right
  // gutter for scrollbar width.
  headerTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    paddingRight: designTokens.spacing[4],
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
  },

  bodyTable: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
  },

  colDate: {
    width: "22%",
  },
  colTrigger: {
    width: "14%",
  },
  colBy: {
    width: "24%",
  },
  colStatus: {
    width: "18%",
  },

  th: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.bold,
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    color: designTokens.neutral[500],
    textAlign: "left" as const,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[2],
    paddingRight: designTokens.spacing[3],
  },

  tr: {
    borderTop: `1px solid ${designTokens.neutral[200]}`,
  },

  td: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[700],
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]} ${designTokens.spacing[2]} 0`,
    whiteSpace: "nowrap" as const,
  },

  statusSuccess: {
    color: designTokens.semantic.success,
    fontWeight: designTokens.fontWeight.semibold,
  },

  statusFailure: {
    color: designTokens.semantic.error,
    fontWeight: designTokens.fontWeight.semibold,
  },

  statusRunning: {
    color: designTokens.neutral[500],
    fontWeight: designTokens.fontWeight.semibold,
  },
});
